import { createClient } from '@supabase/supabase-js';

export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
export const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper function to convert hex color string to valid CSS color
export const stringToColor = (colorString) => {
  if (!colorString) return '#8C52FF'; // Default primary color
  return colorString.startsWith('#') ? colorString : `#${colorString}`;
};

// Get review form with validation
export const getReviewForm = async (reviewFormId, isPreviewMode = false) => {
  try {
    let query = supabase
      .from('review_forms')
      .select('*')
      .eq('id', reviewFormId)
      .eq('is_deleted', false);
    
    // プレビューモードでない場合のみ公開状態をチェック
    if (!isPreviewMode) {
      query = query.eq('is_published', true);
    }
    
    const { data, error } = await query.single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    return null;
  }
};

// Get login screen settings for the form
export const getLoginScreenSettings = async (reviewFormId) => {
  try {
    const { data, error } = await supabase
      .from('login_screen_settings')
      .select('*')
      .eq('review_forms_id', reviewFormId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    return null;
  }
};

// Get form settings (logo, theme, etc.)
export const getReviewFormSettings = async (reviewFormId) => {
  try {
    const { data, error } = await supabase
      .from('review_form_settings')
      .select('*')
      .eq('review_form_id', reviewFormId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    return null;
  }
};

// Check if review form has pages/questions
export const getReviewFormPages = async (reviewFormId) => {
  try {
    const { data, error } = await supabase
      .from('review_form_pages')
      .select('*')
      .eq('review_forms_id', reviewFormId);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

// Helper function to ensure user exists in public.users table (LINE専用に簡略化)
const ensureUserExists = async (authUser) => {
  try {
    // まず、ユーザーがpublic.usersテーブルに存在するか確認
    const { data: existingUser, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('id', authUser.id)
      .single();
    
    // PGRST116 means user not found
    if (checkError && checkError.code === 'PGRST116') {
      // LINEユーザーの場合はdisplayNameも考慮
      const userName = authUser.user_metadata?.full_name || 
                      authUser.user_metadata?.name ||
                      authUser.user_metadata?.displayName || 
                      authUser.email?.split('@')[0] || 
                      'ユーザー';
      
      const userData = {
        id: authUser.id,
        email: authUser.email,
        name: userName,
        line_user_id: authUser.user_metadata?.line_user_id || null,
        created_at: new Date().toISOString()
      };
      
      const { error: insertError } = await supabase
        .from('users')
        .insert([userData]);
      
      if (insertError) {
        console.error('User insert error:', insertError);
        throw insertError;
      }
      
      console.log('User created in public.users table:', userData);
    }
  } catch (error) {
    console.error('ensureUserExists error:', error);
    // エラーが発生してもアプリケーションは続行
  }
};

// Authentication functions
export const signInWithEmail = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    
    // Ensure user exists in public.users table
    if (data.user) {
      await ensureUserExists(data.user);
    }
    
    return { user: data.user, session: data.session };
  } catch (error) {
    throw error;
  }
};

export const signUpWithEmail = async (email, password, name) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: name
        }
      }
    });
    
    if (error) throw error;
    
    // If user is created, also create user record in public.users table
    if (data.user) {
      // Use upsert to avoid conflicts if user already exists
      const { error: userError } = await supabase
        .from('users')
        .upsert([
          {
            id: data.user.id,
            email: email,
            name: name
          }
        ], { 
          onConflict: 'id',
          ignoreDuplicates: false 
        });
      
      if (userError) {
        // Don't throw error here - auth user was created successfully
      }
    }
    
    return { user: data.user, session: data.session };
  } catch (error) {
    throw error;
  }
};

export const signInWithGoogle = async (customRedirectPath = null, preserveParams = true) => {
  try {
    // URLパラメータを取得
    const urlParams = new URLSearchParams(window.location.search);
    const reviewFormId = urlParams.get('reviewFormId');
    const mode = urlParams.get('mode');
    
    let redirectTo;
    
    if (customRedirectPath) {
      // カスタムリダイレクトパスが指定された場合（PreviewLoginPageから）
      redirectTo = customRedirectPath;
    } else {
      // 通常のリダイレクト処理
      if (mode === 'preview') {
        // プレビューモードの場合
        redirectTo = `${window.location.origin}/?reviewFormId=${reviewFormId}&mode=preview`;
      } else {
        // 通常モードの場合
        let questionsUrl = `${window.location.origin}/questions`;
        if (reviewFormId && preserveParams) {
          questionsUrl += `?reviewFormId=${reviewFormId}`;
        }
        redirectTo = questionsUrl;
      }
    }
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
    
    if (error) throw error;
    return data;
  } catch (error) {
    throw error;
  }
};

// LINEログインでユーザーを登録・取得（シンプル版）
export const registerLineUser = async (lineProfile) => {
  try {
    console.log('registerLineUser called with profile:', lineProfile);
    console.log('Supabase URL:', supabaseUrl);
    console.log('Using endpoint:', `${supabaseUrl}/functions/v1/line-register`);
    
    const response = await fetch(`${supabaseUrl}/functions/v1/line-register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`
      },
      body: JSON.stringify({ lineProfile })
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        throw new Error(`Server error: ${errorText}`);
      }
      
      throw new Error(errorData.error || 'LINE registration failed');
    }

    const responseData = await response.json();
    console.log('LINE registration response:', responseData);

    const { user, isNewUser } = responseData;

    if (user) {
      // ユーザー情報をlocalStorageに保存
      localStorage.setItem('line_user', JSON.stringify(user));
      console.log('User saved to localStorage');
    }
    
    return {
      user: user,
      isNewUser: isNewUser
    };
  } catch (error) {
    console.error('LINE registration error:', error);
    console.error('Error stack:', error.stack);
    throw error;
  }
};

export const signOut = async () => {
  try {
    // LINEユーザー情報をクリア
    localStorage.removeItem('line_user');
    
    // 通常のSupabaseサインアウト
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    // LINEユーザーの場合はlocalStorageから取得
    const lineUser = localStorage.getItem('line_user');
    if (lineUser) {
      return JSON.parse(lineUser);
    }
    
    // 通常のSupabase認証（LINE以外のユーザー用）
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    
    // Ensure user exists in public.users table
    if (user) {
      await ensureUserExists(user);
    }
    
    return user;
  } catch (error) {
    return null;
  }
};

// LINEユーザーかどうかを判定
export const isLineUser = (user) => {
  return user?.user_metadata?.is_line_user === true || 
         user?.app_metadata?.provider === 'line';
};

// ユーザーの詳細情報を取得（usersテーブルから）
export const getUserDetails = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user details:', error);
    return null;
  }
};

export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange(callback);
};

// セッション取得関数
export const getSession = async () => {
  try {
    // LINEユーザーの場合はlocalStorageから取得
    const lineUser = localStorage.getItem('line_user');
    if (lineUser) {
      // 擬似セッションを返す
      return {
        user: JSON.parse(lineUser),
        access_token: 'line-user',
        token_type: 'bearer'
      };
    }
    
    // 通常のSupabase認証
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  } catch (error) {
    return null;
  }
};

export const signInAnonymously = async () => {
  try {
    const { data, error } = await supabase.auth.signInAnonymously();
    
    if (error) throw error;
    
    // Create a temporary user record for anonymous users
    if (data.user) {
      await ensureUserExists(data.user, true); // Pass true to indicate anonymous user
    }
    
    return { user: data.user, session: data.session };
  } catch (error) {
    throw error;
  }
};

// Get completion screen settings for the form
export const getCompletionScreenSettings = async (reviewFormId) => {
  try {
    const { data, error } = await supabase
      .from('completion_screen_settings')
      .select('*')
      .eq('review_forms_id', reviewFormId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    return null;
  }
};

// Get question screen settings for the form
export const getQuestionScreenSettings = async (reviewFormId) => {
  try {
    const { data, error } = await supabase
      .from('question_screen_settings')
      .select('*')
      .eq('review_forms_id', reviewFormId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    return null;
  }
};

// Get review questions for a specific page
export const getReviewQuestions = async (reviewFormId, pageId) => {
  try {
    const { data, error } = await supabase
      .from('review_questions')
      .select(`
        *,
        question_option_choices(*),
        question_option_linear_scale(*)
      `)
      .eq('review_fome_id', reviewFormId)
      .eq('review_form_pages_id', pageId)
      .order('question_number', { ascending: true });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

// Get all question option choices for a question
export const getQuestionOptionChoices = async (questionId) => {
  try {
    const { data, error } = await supabase
      .from('question_option_choices')
      .select('*')
      .eq('review_questions_id', questionId)
      .order('choice_number', { ascending: true });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

// Get linear scale settings for a question
export const getQuestionLinearScale = async (questionId) => {
  try {
    const { data, error } = await supabase
      .from('question_option_linear_scale')
      .select('*')
      .eq('review_questions_id', questionId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    return null;
  }
};

// Save review form answers (implementing Flutter app logic)
// New lottery function that calls Edge Function
export const submitAnswersWithLottery = async (reviewFormId, userId, answersData, storeCode = null) => {
  try {
    // First ensure user exists in public.users table
    const { data: existingUser, error: userCheckError } = await supabase
      .from('users')
      .select('id')
      .eq('id', userId)
      .single();

    if (userCheckError && userCheckError.code === 'PGRST116') {
      // User doesn't exist, get auth user info and create
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (authUser) {
        await ensureUserExists(authUser);
      }
    }

    // Now call lottery function
    const { data, error } = await supabase.functions.invoke('lottery', {
      body: { reviewFormId, userId, answersData, storeCode }
    });

    if (error) throw error;

    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error in lottery submission:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Confirm receipt function that calls Edge Function
export const confirmReceipt = async (winnerId) => {
  try {
    const { data, error } = await supabase.functions.invoke('confirm-receipt', {
      body: { winnerId }
    });

    if (error) throw error;

    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error in confirm receipt:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Verify winner token function that calls Edge Function
export const verifyWinnerToken = async (token) => {
  try {
    const { data, error } = await supabase.functions.invoke('verify-winner-token', {
      body: { token }
    });

    if (error) throw error;

    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error in verify winner token:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Keep original function for backward compatibility
export const saveReviewFormAnswers = async (reviewFormId, userId, answersData) => {
  try {
    // 0. Ensure user exists in public.users table before creating submission
    const currentUser = await getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    // 1. Get store_id from store_review_forms table
    console.log('Looking for store_id for reviewFormId:', reviewFormId);
    const { data: storeReviewForm, error: storeError } = await supabase
      .from('store_review_forms')
      .select('store_id')
      .eq('review_form_id', reviewFormId)
      .single();
    
    let storeId;
    if (storeError) {
      console.error('Store lookup error:', storeError);
      
      // If no store relationship exists, create a default store_id
      // This is a fallback to prevent complete failure
      if (storeError.code === 'PGRST116') {
        console.log('No store relationship found, using fallback store_id');
        // Use a default UUID that indicates this is a fallback
        storeId = '00000000-0000-0000-0000-000000000001';
        console.log('Using fallback store_id:', storeId);
      } else {
        throw storeError;
      }
    } else {
      storeId = storeReviewForm.store_id;
      console.log('Found store_id:', storeId);
    }
    
    // 2. Create submission record
    const submissionRecord = {
      review_forms_id: reviewFormId,
      users: userId
    };
    
    // Always include store_id (including fallback value)
    submissionRecord.store_id = storeId;
    
    console.log('Creating submission with record:', submissionRecord);
    const { data: submissionData, error: submissionError } = await supabase
      .from('review_form_submissions')
      .insert([submissionRecord])
      .select()
      .single();
    
    if (submissionError) throw submissionError;
    
    const submissionId = submissionData.id;
    
    // 3. Process each answer
    for (const [questionId, answerData] of Object.entries(answersData)) {
      // Create question answer record
      const questionAnswerRecord = {
        review_form_submissions_id: submissionId,
        review_questions_id: questionId
      };
      
      // Always include store_id (including fallback value)
      questionAnswerRecord.store_id = storeId;
      
      const { data: questionAnswerData, error: questionAnswerError } = await supabase
        .from('review_question_answers')
        .insert([questionAnswerRecord])
        .select()
        .single();
      
      if (questionAnswerError) throw questionAnswerError;
      
      const questionAnswerId = questionAnswerData.id;
      
      // 4. Save specific answer type based on question type
      const questionTypeId = answerData.questionTypeId;
      
      switch (questionTypeId) {
        case 1: // Short text
        case 2: // Long text
          if (answerData.answer) {
            const textAnswerRecord = {
              review_questions_answers_id: questionAnswerId,
              answer_text: answerData.answer
            };
            
            // Always include store_id (including fallback value)
            textAnswerRecord.store_id = storeId;
            
            const { error: textError } = await supabase
              .from('question_answer_texts')
              .insert([textAnswerRecord]);
            if (textError) throw textError;
          }
          break;
          
        case 3: // Single choice
        case 8: // Pull down
          if (answerData.answer) {
            // Find the choice ID based on choice number
            const { data: choiceData, error: choiceError } = await supabase
              .from('question_option_choices')
              .select('id')
              .eq('review_questions_id', questionId)
              .eq('choice_number', parseInt(answerData.answer))
              .single();
            
            if (choiceError) {
              if (choiceError.code === 'PGRST116') {
                continue; // Skip this answer
              }
              throw choiceError;
            }
            
            const choiceAnswerRecord = {
              review_question_answers_id: questionAnswerId,
              question_option_choices_id: choiceData.id
            };
            
            // Always include store_id (including fallback value)
            choiceAnswerRecord.store_id = storeId;
            
            const { error: choiceAnswerError } = await supabase
              .from('question_answer_option_choices')
              .insert([choiceAnswerRecord]);
            if (choiceAnswerError) throw choiceAnswerError;
          }
          break;
          
        case 4: // Multiple choice
        case 5: // Single choice matrix
        case 6: // Multiple choice matrix
          if (answerData.answers && Array.isArray(answerData.answers)) {
            for (const choiceNumber of answerData.answers) {
              // Find the choice ID based on choice number
              const { data: choiceData, error: choiceError } = await supabase
                .from('question_option_choices')
                .select('id')
                .eq('review_questions_id', questionId)
                .eq('choice_number', parseInt(choiceNumber))
                .single();
              
              if (choiceError) {
                if (choiceError.code === 'PGRST116') {
                  continue; // Skip this choice
                }
                throw choiceError;
              }
              
              const choiceAnswerRecord = {
                review_question_answers_id: questionAnswerId,
                question_option_choices_id: choiceData.id
              };
              
              // Always include store_id (including fallback value)
              choiceAnswerRecord.store_id = storeId;
              
              const { error: choiceAnswerError } = await supabase
                .from('question_answer_option_choices')
                .insert([choiceAnswerRecord]);
              if (choiceAnswerError) throw choiceAnswerError;
            }
          }
          break;
          
        case 7: // Linear scale
          if (answerData.answer) {
            const scaleAnswerRecord = {
              review_question_answers_id: questionAnswerId,
              answer_number: parseInt(answerData.answer)
            };
            
            // Always include store_id (including fallback value)
            scaleAnswerRecord.store_id = storeId;
            
            const { error: scaleError } = await supabase
              .from('question_answer_option_linear_scale')
              .insert([scaleAnswerRecord]);
            if (scaleError) throw scaleError;
          }
          break;
          
        default:
      }
    }
    
    return { success: true, submissionId };
  } catch (error) {
    throw error;
  }
};

// Check if current time is within store business hours
export const checkBusinessHours = async (reviewFormId) => {
  try {
    // Get store_id from store_review_forms table
    // Use maybeSingle() instead of single() to handle 0 rows gracefully
    const { data: storeReviewForm, error: storeError } = await supabase
      .from('store_review_forms')
      .select('store_id')
      .eq('review_form_id', reviewFormId)
      .maybeSingle();

    // If no store relationship exists, assume no time restrictions
    if (!storeReviewForm || storeError) {
      console.log('No store relationship found, skipping business hours check');
      return true;
    }

    // Get store business hours
    const { data: storeData, error: storeDataError } = await supabase
      .from('stores')
      .select('opening_time, closing_time')
      .eq('id', storeReviewForm.store_id)
      .maybeSingle();

    // If no store data exists, assume no time restrictions
    if (!storeData || storeDataError) {
      console.log('No store data found, skipping business hours check');
      return true;
    }

    // If opening_time or closing_time is null, assume no time restrictions
    if (!storeData.opening_time || !storeData.closing_time) {
      return true;
    }

    // Get current time in Japan timezone
    const now = new Date();
    const japanTime = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Tokyo',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(now);

    const currentTime = japanTime;
    const openingTime = storeData.opening_time;
    const closingTime = storeData.closing_time;

    // Compare times (format: HH:MM:SS)
    return currentTime >= openingTime && currentTime <= closingTime;
  } catch (error) {
    console.log('Business hours check info:', error);
    // If any error occurs, assume no time restrictions (allow submission)
    return true;
  }
};

// Get store redirect URL by store code
const storeRedirectCache = new Map();

export const getStoreRedirectUrl = async (storeCode) => {
  if (storeRedirectCache.has(storeCode)) return storeRedirectCache.get(storeCode);
  const promise = fetchStoreRedirectUrl(storeCode);
  storeRedirectCache.set(storeCode, promise);
  const result = await promise;
  if (!result?.success) storeRedirectCache.delete(storeCode); // 失敗は次回やり直す
  return result;
};

const fetchStoreRedirectUrl = async (storeCode) => {
  try {
    console.log('Looking up store with code:', storeCode);

    // First, try Edge Function
    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/store-redirect?code=${storeCode}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();
      console.log('Edge Function response:', {
        status: response.status,
        ok: response.ok,
        result
      });

      if (response.ok) {
        return result;
      } else {
        console.error('Edge Function returned error:', result);
      }
    } catch (fetchError) {
      console.log('Edge Function failed, falling back to direct query:', fetchError);
    }

    // Fallback: Direct Supabase query
    // Find the store by store_url_code
    const { data: storeData, error: storeError } = await supabase
      .from('stores')
      .select('id, company_id, companies!inner(line_mini_app_url)')
      .eq('store_url_code', storeCode)
      .single();

    if (storeError || !storeData) {
      console.error('Store not found:', storeError);
      return {
        success: false,
        error: 'Store not found'
      };
    }

    console.log('Found store:', storeData);

    // Find the published review form for this store
    const { data: storeReviewForms, error: formError } = await supabase
      .from('store_review_forms')
      .select(`
        review_form_id,
        is_published,
        created_at,
        review_forms!inner (
          id,
          is_published,
          is_deleted
        )
      `)
      .eq('store_id', storeData.id)
      .eq('is_published', true)
      .eq('review_forms.is_published', true)
      .eq('review_forms.is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(1);

    if (formError || !storeReviewForms || storeReviewForms.length === 0) {
      console.error('No published review form found for store:', formError);
      return {
        success: false,
        error: 'No published review form found for this store'
      };
    }

    const storeReviewForm = storeReviewForms[0];

    console.log('Found review form:', storeReviewForm.review_form_id);

    // The review form is already verified in the join query above, so we can proceed directly

    // Return the review form ID
    return {
      success: true,
      formId: storeReviewForm.review_form_id,
      storeId: storeData.id,
      companyId: storeData.company_id,
      lineMiniAppUrl: storeData.companies?.line_mini_app_url ?? null
    };
  } catch (error) {
    console.error('Error getting store redirect:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all questions for a review form (all pages)
export const getAllReviewQuestions = async (reviewFormId) => {
  try {
    const { data, error } = await supabase
      .from('review_questions')
      .select(`
        *,
        question_option_choices(*),
        question_option_linear_scale(*)
      `)
      .eq('review_fome_id', reviewFormId)
      .order('pege_number', { ascending: true })
      .order('question_number', { ascending: true });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    return [];
  }
};

// Check if user is eligible to answer (cooldown days are looked up per store)
export const checkAnswerEligibility = async (reviewFormId, userId, storeCode = null) => {
  try {
    const { data, error } = await supabase.functions.invoke('check-eligibility', {
      body: { reviewFormId, userId, storeCode: storeCode || undefined }
    });

    if (error) throw error;

    return {
      success: true,
      isEligible: data.isEligible,
      message: data.message,
      cooldownDays: data.cooldownDays,
      visitorType: data.visitorType
    };
  } catch (error) {
    console.error('Error checking eligibility:', error);
    // エラーの場合は回答可能として扱う（チェックをスキップ、初回向けを既定とする）
    return {
      success: true,
      isEligible: true,
      visitorType: 'first'
    };
  }
};